// dialogs.js — app-styled replacements for window.alert/confirm/prompt.
//
// The native dialogs are ugly in an Android WebView (they carry a
// "localhost says…" title) and prompt() is not even guaranteed to work there.
// DialogHost.svelte (mounted once in App.svelte) renders everything below.
import { writable, get } from 'svelte/store';

export const toasts = writable([]);
export const dialogState = writable(null);

let _toastId = 0;

/**
 * Non-blocking notification. type: 'info' | 'success' | 'error'.
 * Use for everything that previously was an informational alert().
 */
export function toast(message, type = 'info', duration = 3500) {
  const id = ++_toastId;
  toasts.update(list => [...list.slice(-2), { id, message: String(message), type, duration }]);
  setTimeout(() => toasts.update(list => list.filter(t => t.id !== id)), duration);
}

function _openDialog(config) {
  return new Promise((resolve, reject) => {
    const prev = get(dialogState);
    if (prev && typeof prev.reject === 'function') prev.reject(new Error('Dialog overwritten'));
    dialogState.set({ ...config, resolve, reject });
  });
}

/** Await-able OK/Cancel question. Resolves true/false. */
export function confirmDialog(message, opts = {}) {
  return _openDialog({
    type: 'confirm',
    message: String(message),
    title: opts.title || 'Confirm',
    okText: opts.okText || 'OK',
    cancelText: opts.cancelText || 'Cancel',
    danger: !!opts.danger
  });
}

/** Await-able text input. Resolves the string, or null on cancel. */
export function promptDialog(message, opts = {}) {
  return _openDialog({
    type: 'prompt',
    message: String(message),
    title: opts.title || '',
    value: opts.value || '',
    placeholder: opts.placeholder || '',
    okText: opts.okText || 'OK',
    cancelText: opts.cancelText || 'Cancel'
  });
}

/** Await-able acknowledgement — for messages the user must not miss. */
export function alertDialog(message, opts = {}) {
  return _openDialog({
    type: 'alert',
    message: String(message),
    title: opts.title || '',
    okText: opts.okText || 'OK'
  });
}

/** Internal — DialogHost calls this to settle the active dialog. */
export function _settleDialog(state, result) {
  if (state && typeof state.resolve === 'function') state.resolve(result);
  dialogState.set(null);
}
